import React, { useState, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Snackbar,
    Alert,
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    Paper,
    IconButton,
    MenuItem,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
    createNotice,
    getPendingNotices,
    approveNotice,
    updateNotice,
    rejectNotice,
    deleteNotice,
    getUserNotices
} from '../db/noticeApi';
import { Timestamp } from 'firebase/firestore';
import { uploadNoticeFileToStorage } from "../db/uploadNoticeFileToStorage";
import { getFacilitiesByTenant } from "../db/facilityApi";
import { getTenantById } from "../db/tenantApi";

const NoticeManagement = () => {
    const { user } = useAuth();
    const [tab, setTab] = useState(0);
    const [viewSignature, setViewSignature] = useState(false);
    const [viewAttachment, setViewAttachment] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [yourNotices, setYourNotices] = useState([]);
    const [attachmentPreview, setAttachmentPreview] = useState([]);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [activeAttachment, setActiveAttachment] = useState(null);
    const [scopeOptions, setScopeOptions] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [pendingNotices, setPendingNotices] = useState([]);
    const getDefaultTargetDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        return date.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        targetDate: getDefaultTargetDate(),
        scope: '',
        type: 'Notice'
    });
    const [signatureFile, setSignatureFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const loadData = async () => {
        if (!user?.tenantId) return;

        // Approval pending
        const pending = await getPendingNotices(
            user.tenantId,
            user.facilityId,
            user.role
        );
        setPendingNotices(pending);

        // All notices created by user
        const userData = await getUserNotices(user.uid);
        setYourNotices(userData);
    };
    const [editingId, setEditingId] = useState(null);
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const loadScopes = async () => {

        try {

            const facilities = await getFacilitiesByTenant(user.tenantId);

            const facilityScopes = facilities.map(f => {

                const name = f.fullName || f.facilityName || f.shortName || "Unknown";

                return {
                    value: `facility:${name}`,
                    label: `Facility: ${name}`
                };

            });

            const tenant = await getTenantById(user.tenantId);
            const tenantName = tenant?.tenantName || "Tenant";
            const tenantScope = {
                value: `tenant:${tenantName}`,
                label: `Tenant: ${tenantName}`
            };

            // ✅ FIX: no tenantScopes here
            setScopeOptions([
                tenantScope,
                ...facilityScopes
            ]);

            // ✅ IMPORTANT: set default value
            setFormData(prev => ({
                ...prev,
                scope: tenantScope.value
            }));

        } catch (error) {

            console.error("Error loading scopes", error);

        }

    };

    useEffect(() => {
        if (user) {
            loadData();
            loadScopes();
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.content || !formData.targetDate) {
            showSnackbar('All fields are required', 'error');
            return;
        }

        try {
            setLoading(true);

            let signatureUrl = '';

            if (signatureFile) {
                signatureUrl = await uploadNoticeFileToStorage(
                    signatureFile,
                    user.uid
                );
            }

            let uploadedAttachments = [];

            if (attachments.length > 0) {
                for (const file of attachments) {
                    const url = await uploadNoticeFileToStorage(
                        file,
                        user.uid
                    );

                    uploadedAttachments.push({
                        name: file.name,
                        url,
                        type: file.type.includes('pdf') ? 'pdf' : 'image'
                    });
                }
            }

            if (editingId) {
                await updateNotice(editingId, {
                    title: formData.title,
                    content: formData.content,
                    targetDate: Timestamp.fromDate(new Date(formData.targetDate)),
                    scope: formData.scope,
                    scopeType: formData.scope?.split(":")[0] || '',
                    scopeName: formData.scope?.split(":")[1] || '',
                    ...(signatureUrl && { signatureUrl })
                });
                showSnackbar('Notice Updated', 'success');
                setEditingId(null);
            } else {
                await createNotice({
                    title: formData.title,
                    content: formData.content,
                    type: formData.type,
                    targetDate: Timestamp.fromDate(new Date(formData.targetDate)),
                    scope: formData.scope,
                    scopeType: formData.scope.split(":")[0],
                    scopeName: formData.scope.split(":")[1],
                    tenantId: user.tenantId || null,
                    facilityId: user.facilityId || null,
                    createdBy: user.uid,
                    createdByRole: user.role,
                    signatureUrl,
                    attachments: uploadedAttachments
                });
                showSnackbar('Notice created', 'success');
                setSignaturePreview(null);
                setAttachmentPreview([]);
                setAttachments([]);
                setSignatureFile(null);
                await loadData();
            }

            setFormData({
                title: '',
                content: '',
                targetDate: getDefaultTargetDate(),
                scope: 'facility',
                type: 'Notice'
            });

        } catch (error) {
            showSnackbar('Error saving notice', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (notice) => {
        if (notice.scope === 'tenancy' && user.role !== 'appadmin') {
            showSnackbar('Only Tenant Admin can approve Tenancy scope notices', 'error');
            return;
        }

        await approveNotice(notice.id, user.uid);
        showSnackbar('Notice approved', 'success');
        await loadData();
    };

    return (
        <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
            <Container maxWidth="lg" sx={{ mt: 4 }}>

                {/* HEADER */}
                <Typography sx={{
                    fontSize: "2rem",
                    fontWeight: 900,
                    fontFamily: "'Unbounded', sans-serif",
                    color: "#1c1400",
                    mb: 1
                }}>
                    Notice Management
                </Typography>

                <Typography sx={{ color: "#8a7a40", mb: 3 }}>
                    Create, manage and publish notices efficiently
                </Typography>

                <Paper sx={{
                    p: 4,
                    borderRadius: "16px",
                    border: "2px solid #f0d060",
                    background: "#ffffff"
                }}>

                    {/* TABS */}
                    <Tabs
                        value={tab}
                        onChange={(e, newValue) => setTab(newValue)}
                        sx={{
                            mb: 4,
                            "& .MuiTabs-indicator": {
                                backgroundColor: "#1c1400"
                            }
                        }}
                    >
                        <Tab label="Create Notice" />
                        {(user.role === 'appadmin' || user.role === 'facilityadmin') && (
                            <Tab label="Notice Approval" />
                        )}
                    </Tabs>

                    {/* CREATE TAB */}
                    {tab === 0 && (
                        <>
                            <Paper
                                sx={{
                                    p: 5,
                                    mb: 5,
                                    borderRadius: "16px",
                                    border: "1px solid #f0d060",
                                    background: "#ffffff"
                                }}
                            >
                                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                                    Create Notice
                                </Typography>

                                <Typography sx={{ mb: 4, color: "#6b7280" }}>
                                    Fill the details below to publish a notice.
                                </Typography>

                                <Grid container spacing={4}>

                                    {/* TITLE */}
                                    <Grid item xs={12}>
                                        <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                            Notice Title
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            name="title"
                                            value={formData.title}
                                            onChange={(e) => {
                                                if (e.target.value.length <= 200) handleChange(e);
                                            }}
                                            helperText={`${formData.title.length}/200`}
                                            inputProps={{ maxLength: 200 }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        />
                                    </Grid>

                                    {/* CONTENT */}
                                    <Grid item xs={12}>
                                        <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                            Notice Content
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            name="content"
                                            value={formData.content}
                                            onChange={(e) => {
                                                if (e.target.value.length <= 1000) handleChange(e);
                                            }}
                                            helperText={`${formData.content.length}/1000`}
                                            inputProps={{ maxLength: 1000 }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        />
                                    </Grid>

                                    {/* TYPE */}
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                            Notice Type
                                        </Typography>

                                        <TextField
                                            select
                                            fullWidth
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        >
                                            <MenuItem value="Notice">Notice</MenuItem>
                                            <MenuItem value="Information">Information</MenuItem>
                                        </TextField>
                                    </Grid>

                                    {/* DATE */}
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                            Target Date
                                        </Typography>

                                        <TextField
                                            type="date"
                                            fullWidth
                                            name="targetDate"
                                            value={formData.targetDate}
                                            InputLabelProps={{ shrink: true }}
                                            onChange={handleChange}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        />
                                    </Grid>

                                    {/* SCOPE */}
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                            Notice Scope
                                        </Typography>

                                        <TextField
                                            select
                                            fullWidth
                                            name="scope"
                                            value={formData.scope || ''}
                                            onChange={handleChange}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        >
                                            {scopeOptions.map((scope, index) => (
                                                <MenuItem key={index} value={scope.value}>
                                                    {scope.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    {/* FILE UPLOAD */}
                                    {/* FILE UPLOAD SECTION */}
                                    <Grid item xs={12}>

                                        <Paper
                                            sx={{
                                                p: 3,
                                                borderRadius: "12px",
                                                background: "#fffbea",
                                                border: "1px dashed #f0d060"
                                            }}
                                        >

                                            <Typography sx={{ mb: 2, fontWeight: 600 }}>
                                                Attachments & Signature
                                            </Typography>

                                            {/* Signature Preview */}
                                            {signaturePreview && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="caption">Signature</Typography>

                                                    <Box
                                                        component="img"
                                                        src={signaturePreview}
                                                        sx={{
                                                            display: "block",
                                                            maxHeight: 80,
                                                            mt: 1,
                                                            borderRadius: "6px"
                                                        }}
                                                    />
                                                </Box>
                                            )}

                                            <Box sx={{
                                                display: "flex",
                                                gap: 3,
                                                flexWrap: "wrap"
                                            }}>

                                                {/* SIGNATURE UPLOAD */}
                                                <Box
                                                    sx={{
                                                        flex: "1 1 250px",
                                                        p: 2,
                                                        borderRadius: "12px",
                                                        border: "1px solid #f0d060",
                                                        background: "#ffffff",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            borderColor: "#f59e0b",
                                                            background: "#fffbea"
                                                        }
                                                    }}
                                                >
                                                    <Button component="label" sx={{ textTransform: "none" }}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 600 }}>
                                                                Upload Signature
                                                            </Typography>

                                                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                                                                Max size: 30 KB
                                                            </Typography>

                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept="image/*"
                                                                onChange={(e) => {

                                                                    const file = e.target.files[0];
                                                                    if (!file) return;

                                                                    const maxSize = 30 * 1024;

                                                                    if (file.size > maxSize) {
                                                                        showSnackbar("Signature must be under 30 KB", "error");
                                                                        return;
                                                                    }

                                                                    setSignatureFile(file);
                                                                    setSignaturePreview(URL.createObjectURL(file));

                                                                }}
                                                            />
                                                        </Box>
                                                    </Button>
                                                </Box>

                                                {/* ATTACHMENT UPLOAD */}
                                                <Box
                                                    sx={{
                                                        flex: "1 1 250px",
                                                        p: 2,
                                                        borderRadius: "12px",
                                                        border: "1px solid #f0d060",
                                                        background: "#ffffff",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            borderColor: "#f59e0b",
                                                            background: "#fffbea"
                                                        }
                                                    }}
                                                >
                                                    <Button component="label" sx={{ textTransform: "none" }}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 600 }}>
                                                                Upload Attachments
                                                            </Typography>

                                                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                                                                Images / PDF • Max 100KB
                                                            </Typography>

                                                            <input
                                                                type="file"
                                                                hidden
                                                                multiple
                                                                accept="image/*,application/pdf"
                                                                onChange={(e) => {

                                                                    const files = Array.from(e.target.files);
                                                                    const maxSize = 100 * 1024;
                                                                    const maxFiles = 5;

                                                                    let newAttachments = [...attachments];
                                                                    let newPreviews = [...attachmentPreview];

                                                                    files.forEach((file) => {

                                                                        if (newAttachments.length >= maxFiles) {
                                                                            showSnackbar("Maximum 5 attachments allowed", "error");
                                                                            return;
                                                                        }

                                                                        if (file.size > maxSize) {
                                                                            showSnackbar(`${file.name} exceeds 100 KB`, "error");
                                                                            return;
                                                                        }

                                                                        newAttachments.push(file);

                                                                        newPreviews.push({
                                                                            file,
                                                                            url: URL.createObjectURL(file),
                                                                            type: file.type.includes("pdf") ? "pdf" : "image"
                                                                        });

                                                                    });

                                                                    setAttachments(newAttachments);
                                                                    setAttachmentPreview(newPreviews);

                                                                }}
                                                            />
                                                        </Box>
                                                    </Button>
                                                </Box>

                                                {/* VIEW BUTTON (UNCHANGED LOGIC) */}
                                                {attachmentPreview.length > 0 && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<VisibilityIcon />}
                                                        sx={{
                                                            alignSelf: "center",
                                                            background: "#1c1400",
                                                            color: "#fde047",
                                                            "&:hover": {
                                                                background: "#f59e0b",
                                                                color: "#1c1400"
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            setActiveAttachment(attachmentPreview[0]);
                                                            setViewAttachment(true);
                                                        }}
                                                    >
                                                        View ({attachmentPreview.length})
                                                    </Button>
                                                )}

                                            </Box>

                                        </Paper>

                                    </Grid>

                                    {/* ACTION BUTTONS */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: "flex", gap: 2 }}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={() =>
                                                    setSelectedNotice({
                                                        ...formData,
                                                        createdAt: new Date(),
                                                        signatureUrl: signaturePreview,
                                                        attachments: attachmentPreview,
                                                        status: "preview"
                                                    })
                                                }
                                                sx={{
                                                    borderColor: "#1c1400",
                                                    color: "#1c1400",
                                                    "&:hover": {
                                                        borderColor: "#f59e0b",
                                                        color: "#f59e0b"
                                                    }
                                                }}
                                            >
                                                Preview Notice
                                            </Button>

                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onClick={handleSubmit}
                                                sx={{
                                                    background: "#1c1400",
                                                    color: "#fde047",
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        background: "#f59e0b",
                                                        color: "#1c1400"
                                                    }
                                                }}
                                            >
                                                {loading ? "Submitting..." : "Submit Notice"}
                                            </Button>
                                        </Box>
                                    </Grid>

                                </Grid>
                            </Paper>


                            {/* YOUR PENDING NOTICES */}
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Your Notices
                            </Typography>

                            <Grid container spacing={3}>
                                {yourNotices
                                    .filter(n => n.createdBy === user.uid)
                                    .map((notice) => (
                                        <Grid item xs={12} md={6} key={notice.id}>
                                            <Paper
                                                sx={{
                                                    p: 3,
                                                    borderRadius: 3,
                                                    borderLeft: `6px solid ${notice.status === 'pending'
                                                        ? '#fbc02d'
                                                        : notice.status === 'approved'
                                                            ? '#2e7d32'
                                                            : notice.status === 'rejected'
                                                                ? '#c62828'
                                                                : '#757575'
                                                        }`,
                                                    background:
                                                        notice.status === 'pending'
                                                            ? '#fffde7'
                                                            : notice.status === 'approved'
                                                                ? '#f1f8e9'
                                                                : notice.status === 'rejected'
                                                                    ? '#ffebee'
                                                                    : '#f5f5f5',
                                                    position: 'relative',
                                                    transition: '0.3s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: 4
                                                    }
                                                }}
                                            >

                                                {/* STATUS RIBBON */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 15,
                                                        right: 15,
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 20,
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: '#fff',
                                                        background:
                                                            notice.status === 'pending'
                                                                ? '#fbc02d'
                                                                : notice.status === 'approved'
                                                                    ? '#2e7d32'
                                                                    : notice.status === 'rejected'
                                                                        ? '#c62828'
                                                                        : '#757575'
                                                    }}
                                                >
                                                    {notice.status.toUpperCase()}
                                                </Box>

                                                {/* TITLE */}
                                                <Typography variant="h6" sx={{ mb: 1 }}>
                                                    {notice.title}
                                                </Typography>

                                                <Typography variant="body2" sx={{ mb: 2 }}>
                                                    Scope: <strong>{notice.scope}</strong>
                                                </Typography>

                                                {/* ACTIONS */}
                                                {notice.status === 'pending' ? (
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <IconButton
                                                            size="info"
                                                            sx={{ background: '#e3f2fd' }}
                                                            onClick={() => setSelectedNotice(notice)}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>

                                                        <IconButton
                                                            color="primary"
                                                            sx={{ background: '#e3f2fd' }}
                                                            onClick={() => {
                                                                setFormData({
                                                                    title: notice.title,
                                                                    content: notice.content,
                                                                    targetDate: notice.targetDate
                                                                        ?.toDate()
                                                                        .toISOString()
                                                                        .split('T')[0],
                                                                    scope: notice.scope
                                                                });
                                                                setEditingId(notice.id);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>

                                                        <IconButton
                                                            color="error"
                                                            sx={{ background: '#ffebee' }}
                                                            onClick={async () => {
                                                                await deleteNotice(notice.id);
                                                                await loadData();
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            mt: 1,
                                                            p: 1.5,
                                                            borderRadius: 2,
                                                            background: 'rgba(0,0,0,0.05)',
                                                            fontSize: 13
                                                        }}
                                                    >
                                                        This notice has been finalized and can no longer be modified.
                                                    </Box>
                                                )}

                                            </Paper>
                                        </Grid>
                                    ))}
                            </Grid>
                        </>
                    )}

                    {/* ================= APPROVAL TAB ================= */}
                    {tab === 1 && (
                        <>
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Pending Approvals
                            </Typography>

                            <Grid container spacing={3}>
                                {pendingNotices.map((notice) => (
                                    <Grid item xs={12} md={6} key={notice.id}>
                                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                                            <Typography fontWeight={600}>
                                                {notice.title}
                                            </Typography>

                                            <Typography variant="body2">
                                                Scope: {notice.scope}
                                            </Typography>

                                            <Typography variant="body2">
                                                Created By: {notice.createdByRole}
                                            </Typography>

                                            <Box sx={{ mt: 2 }}>
                                                {(notice.scope === 'facility' &&
                                                    (user.role === 'appadmin' ||
                                                        user.role === 'facilityadmin')) ||
                                                    (notice.scope === 'tenancy' &&
                                                        user.role === 'appadmin') ? (
                                                    <>
                                                        <IconButton
                                                            size="info"
                                                            sx={{ background: '#e3f2fd' }}
                                                            onClick={() => setSelectedNotice(notice)}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            color="success"
                                                            onClick={() => handleApprove(notice)}
                                                        >
                                                            <CheckIcon />
                                                        </IconButton>

                                                        <IconButton
                                                            color="error"
                                                            onClick={async () => {
                                                                await rejectNotice(
                                                                    notice.id,
                                                                    user.uid
                                                                );
                                                                await loadData();
                                                            }}
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </>
                                                ) : null}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}

                                {pendingNotices.length === 0 && (
                                    <Grid item xs={12}>
                                        <Typography align="center">
                                            No pending notices
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}

                </Paper>

                <Dialog
                    open={Boolean(selectedNotice)}
                    onClose={() => setSelectedNotice(null)}
                    maxWidth="md"
                    fullWidth
                >

                    <DialogContent sx={{ p: 0, background: '#f5f5f5' }}>

                        <Box
                            sx={{
                                background: '#ffffff',
                                p: 5,
                                minHeight: 500,
                                position: 'relative',
                                fontFamily: 'Georgia, serif'
                            }}
                        >

                            {/* WATERMARK */}
                            {(selectedNotice?.status === 'withdrawn' ||
                                selectedNotice?.status === 'rejected' ||
                                selectedNotice?.status === 'marked_for_deletion') && (
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            top: '40%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%) rotate(-30deg)',
                                            fontSize: '4rem',
                                            color: 'rgba(255,0,0,0.1)',
                                            fontWeight: 800,
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        {selectedNotice.status === 'marked_for_deletion'
                                            ? 'EXPIRED'
                                            : selectedNotice.status.toUpperCase()}
                                    </Typography>
                                )}

                            {/* HEADER */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {selectedNotice?.type?.toUpperCase() || "NOTICE"}
                                </Typography>

                                {selectedNotice?.createdAt && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Published on {
                                            selectedNotice.createdAt?.toDate
                                                ? selectedNotice.createdAt.toDate().toLocaleDateString()
                                                : new Date(selectedNotice.createdAt).toLocaleDateString()
                                        }
                                    </Typography>
                                )}

                            </Box>

                            <Box sx={{ borderBottom: '2px solid #000', mb: 3 }} />

                            {/* TITLE */}
                            <Typography
                                variant="h6"
                                sx={{
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    mb: 3,
                                    textDecoration:
                                        selectedNotice?.status === 'withdrawn' ||
                                            selectedNotice?.status === 'marked_for_deletion' ||
                                            selectedNotice?.status === 'rejected'
                                            ? 'line-through'
                                            : 'none'
                                }}
                            >
                                {selectedNotice?.title}
                            </Typography>

                            {/* CONTENT */}
                            <Typography
                                variant="body1"
                                sx={{
                                    whiteSpace: 'pre-line',
                                    lineHeight: 1.8,
                                    mb: 4
                                }}
                            >
                                {selectedNotice?.content}
                            </Typography>

                            {/* TARGET DATE */}
                            {selectedNotice?.targetDate && (
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Target Date: {
                                        selectedNotice?.targetDate?.toDate
                                            ? selectedNotice.targetDate.toDate().toLocaleDateString()
                                            : new Date(selectedNotice.targetDate).toLocaleDateString()
                                    }
                                </Typography>
                            )}

                            {/* SIGNATURE */}
                            {selectedNotice?.signatureUrl && (
                                <Box sx={{ textAlign: 'right', mt: 5 }}>
                                    <Typography variant="subtitle2">
                                        Authorized Signatory
                                    </Typography>

                                    <img
                                        src={selectedNotice.signatureUrl}
                                        alt="Signature"
                                        style={{
                                            maxWidth: '200px',
                                            marginTop: '10px'
                                        }}
                                    />
                                </Box>
                            )}

                            {/* ATTACHMENTS */}
                            {selectedNotice?.attachments?.length > 0 && (
                                <Box sx={{ mt: 5 }}>

                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Attachments:
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        sx={{ mt: 1 }}
                                        onClick={() => {

                                            setAttachmentPreview(selectedNotice.attachments);
                                            setActiveAttachment(selectedNotice.attachments[0]);
                                            setViewAttachment(true);

                                        }}
                                    >

                                        View Attachments ({selectedNotice.attachments.length})

                                    </Button>

                                </Box>
                            )}

                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: 'space-between' }}>

                        <Typography variant="caption" sx={{ ml: 2 }}>
                            Scope: {selectedNotice?.scope}
                        </Typography>

                        <Button onClick={() => setSelectedNotice(null)}>
                            Close
                        </Button>

                    </DialogActions>
                </Dialog>

                <Dialog
                    open={viewAttachment}
                    onClose={() => setViewAttachment(false)}
                    maxWidth="lg"
                    fullWidth
                >

                    <DialogTitle>
                        Attachments ({attachmentPreview.length})
                    </DialogTitle>

                    <DialogContent>

                        <Grid container spacing={2}>

                            {/* LEFT PANEL - FILE LIST */}

                            <Grid item xs={4}>

                                <Box
                                    sx={{
                                        borderRight: "1px solid #ddd",
                                        height: 500,
                                        overflowY: "auto"
                                    }}
                                >

                                    {attachmentPreview.map((file, index) => (

                                        <Box
                                            key={index}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                p: 2,
                                                cursor: "pointer",
                                                borderBottom: "1px solid #eee",
                                                background:
                                                    activeAttachment?.url === file.url
                                                        ? "#e3f2fd"
                                                        : "transparent",
                                                "&:hover": { background: "#f5f5f5" }
                                            }}
                                        >

                                            {/* FILE NAME */}
                                            <Box
                                                onClick={() => setActiveAttachment(file)}
                                                sx={{ flex: 1 }}
                                            >

                                                <Typography variant="body2">

                                                    {file.type.includes("image")
                                                        ? "🖼 Image"
                                                        : "📄 PDF"} — {file.file?.name || file.name}

                                                </Typography>

                                            </Box>

                                            {/* DELETE BUTTON */}

                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => {

                                                    e.stopPropagation();

                                                    const updatedAttachments = attachments.filter((_, i) => i !== index);
                                                    const updatedPreviews = attachmentPreview.filter((_, i) => i !== index);

                                                    setAttachments(updatedAttachments);
                                                    setAttachmentPreview(updatedPreviews);

                                                    if (activeAttachment?.url === file.url) {
                                                        setActiveAttachment(updatedPreviews[0] || null);
                                                    }

                                                }}
                                            >

                                                <DeleteIcon fontSize="small" />

                                            </IconButton>

                                        </Box>

                                    ))}

                                </Box>

                            </Grid>

                            {/* RIGHT PANEL - PREVIEW */}

                            <Grid item xs={8}>

                                <Box
                                    sx={{
                                        height: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >

                                    {activeAttachment ? (

                                        activeAttachment.type.includes("image") ? (

                                            <img
                                                src={activeAttachment.url}
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    borderRadius: 6
                                                }}
                                            />

                                        ) : (

                                            <iframe
                                                src={activeAttachment.url}
                                                width="100%"
                                                height="100%"
                                                style={{ border: "none" }}
                                            />

                                        )

                                    ) : (

                                        <Typography>Select a file to preview</Typography>

                                    )}

                                </Box>

                            </Grid>

                        </Grid>

                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setViewAttachment(false)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() =>
                        setSnackbar({ ...snackbar, open: false })
                    }
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Container>
        </Box>
    );
};

export default NoticeManagement;